<template>
  <div class="custom-image-preview" v-if="visible" @click="closePreview">
    <div class="preview-wrapper" @click.stop>
      <div 
        class="image-drag-container"
        @mousedown="startDrag"
        @mousemove="onDrag"
        @mouseup="stopDrag"
        @mouseleave="stopDrag"
        @wheel="handleWheel"
      >
        <img 
          :src="imageUrl" 
          class="preview-image" 
          :style="{ 
            transform: `translate(${dragPosition.x}px, ${dragPosition.y}px) scale(${imageScale})`,
            cursor: isDragging ? 'grabbing' : 'grab'
          }"
          @dblclick="handleImageDoubleClick"
        />
      </div>
      <div class="preview-close" @click="closePreview">
        <i class="el-icon-close"></i>
      </div>
      <div class="preview-zoom-controls">
        <el-button type="text" @click="zoomImage('in')" icon="el-icon-zoom-in"></el-button>
        <span class="zoom-percentage">{{ Math.round(imageScale * 100) }}%</span>
        <el-button type="text" @click="zoomImage('out')" icon="el-icon-zoom-out"></el-button>
        <el-button type="text" @click="resetZoom" icon="el-icon-refresh-right"></el-button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ImagePreview',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    imageUrl: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      imageScale: 1,
      isDragging: false,
      dragPosition: { x: 0, y: 0 },
      dragStart: { x: 0, y: 0 },
      lastPosition: { x: 0, y: 0 }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetZoom();
      }
    }
  },
  methods: {
    // 关闭图片预览
    closePreview() {
      this.$emit('update:visible', false);
      this.resetZoom();
    },
    
    // 缩放图片
    zoomImage(type) {
      if (type === 'in') {
        this.imageScale = Math.min(5, this.imageScale + 0.2);
      } else if (type === 'out' && this.imageScale > 0.4) {
        this.imageScale = Math.max(0.2, this.imageScale - 0.2);
      }
    },
    
    // 重置缩放和位置
    resetZoom() {
      this.imageScale = 1;
      this.dragPosition = { x: 0, y: 0 };
      this.lastPosition = { x: 0, y: 0 };
    },
    
    // 开始拖动
    startDrag(event) {
      this.isDragging = true;
      this.dragStart = {
        x: event.clientX,
        y: event.clientY
      };
    },
    
    // 拖动中
    onDrag(event) {
      if (!this.isDragging) return;
      
      const deltaX = event.clientX - this.dragStart.x;
      const deltaY = event.clientY - this.dragStart.y;
      
      this.dragPosition = {
        x: this.lastPosition.x + deltaX,
        y: this.lastPosition.y + deltaY
      };
    },
    
    // 停止拖动
    stopDrag() {
      if (this.isDragging) {
        this.lastPosition = { ...this.dragPosition };
        this.isDragging = false;
      }
    },
    
    // 处理滚轮缩放
    handleWheel(event) {
      event.preventDefault();
      
      // 确定缩放方向
      const delta = event.deltaY < 0 ? 0.1 : -0.1;
      const newScale = Math.max(0.2, Math.min(5, this.imageScale + delta));
      
      // 计算鼠标相对于图片的位置
      const rect = event.target.getBoundingClientRect();
      const mouseX = event.clientX - rect.left;
      const mouseY = event.clientY - rect.top;
      
      // 计算缩放前鼠标在图片上的相对位置
      const relativeX = (mouseX - this.dragPosition.x) / this.imageScale;
      const relativeY = (mouseY - this.dragPosition.y) / this.imageScale;
      
      // 计算缩放后的新位置，保持鼠标指向的点不变
      const newPositionX = mouseX - relativeX * newScale;
      const newPositionY = mouseY - relativeY * newScale;
      
      // 更新位置和缩放
      this.dragPosition.x = newPositionX;
      this.dragPosition.y = newPositionY;
      this.lastPosition = { ...this.dragPosition };
      this.imageScale = newScale;
    },
    
    // 双击图片重置
    handleImageDoubleClick() {
      this.resetZoom();
    }
  }
}
</script>

<style scoped>
/* 图片预览样式 */
.custom-image-preview {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.preview-wrapper {
  position: relative;
  width: 90%;
  height: 90%;
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.image-drag-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-image {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  transform-origin: center;
  transition: transform 0.1s ease;
  user-select: none;
  -webkit-user-drag: none;
}

.preview-close {
  position: absolute;
  top: 10px;
  right: 10px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: rgba(0, 0, 0, 0.5);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background-color 0.3s;
  z-index: 10;
}

.preview-close:hover {
  background-color: rgba(0, 0, 0, 0.8);
}

.preview-close i {
  font-size: 20px;
}

.preview-zoom-controls {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  background-color: rgba(0, 0, 0, 0.5);
  border-radius: 20px;
  padding: 5px 15px;
}

.preview-zoom-controls .el-button {
  color: white;
  font-size: 18px;
}

.preview-zoom-controls .el-button:hover {
  color: #409EFF;
}

.zoom-percentage {
  color: white;
  font-size: 14px;
  margin: 0 5px;
  min-width: 45px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
